import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { CardContainer } from "@/components/card-container";
import { ErrorRetryComponent } from "@/components/error-card/error-retry";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvailablePaymentMethods } from "@/features/payment-methods";
import { AppRoute } from "@/types/routes";

import { PaymentMethodForm } from "../../components/payment-method-form";
import { getForm } from "../../helpers/get-form";
import { asyncValidators } from "../../services/validator";

export const PaymentMethodPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { paymentMethodName, formName } = useParams<{
    paymentMethodName: string;
    formName: string;
  }>();

  const { availablePaymentMethods, isLoadingAvailablePaymentMethods } =
    useAvailablePaymentMethods();
  const paymentMethod = availablePaymentMethods.find(
    (method) => method.method_provider_name === paymentMethodName
  );

  const currentForm = formName
    ? getForm(formName, paymentMethod?.form)
    : paymentMethod?.form;

  const handleSubmit = () => {
    if (!currentForm?.next) {
      navigate(`/payment-methods/${paymentMethodName}/preview`);
      return;
    }

    navigate(`/payment-methods/${paymentMethodName}/create/address`);
  };

  const noPaymentMethods = !paymentMethod && !isLoadingAvailablePaymentMethods;
  const noCurrentForm = !currentForm && !isLoadingAvailablePaymentMethods;
  if (noPaymentMethods || noCurrentForm) {
    return (
      <ErrorRetryComponent
        retry={() => navigate(AppRoute.PAYMENT_METHODS_LIST)}
      />
    );
  }

  const renderContent = () => {
    if (isLoadingAvailablePaymentMethods) {
      return (
        <div
          className="flex flex-col space-y-3"
          role="status"
          aria-label="Loading payment method form"
        >
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <div className="pt-2">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      );
    }

    if (currentForm && paymentMethod) {
      return (
        <PaymentMethodForm
          paymentMethod={paymentMethod}
          form={currentForm}
          onSubmit={handleSubmit}
          asyncValidators={asyncValidators}
        />
      );
    }

    return null;
  };

  return (
    <CardContainer
      showBorder={true}
      isLoading={isLoadingAvailablePaymentMethods}
    >
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>
            {paymentMethod?.title ?? t("mileageReview_PaymentMethod")}
          </CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("payment_dynamic_description")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>
      <CardContainer.Content>{renderContent()}</CardContainer.Content>
    </CardContainer>
  );
};
